import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../../core/tools/interfaces/ITool';
import { ICommand } from '../../../core/commands/ICommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Lock, Unlock, Plus, Minus } from 'lucide-react';
import { ChangeNodeStyleCommand } from '../commands/ChangeNodeStyleCommand';
import { useTranslation } from '../../../hooks/useTranslation';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

/**
 * Person style property panel component
 */
const PersonStylePropertyComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('genogram');
  const selectedIds = Array.from(context.selection);
  const nodes = useDiagramStore(state => state.nodes);
  const selectedPersons = selectedIds.map(id => nodes.find(node => node.id === id) as any).filter(Boolean);

  const [isOpen, setIsOpen] = React.useState(() => {
    try {
      const saved = localStorage.getItem('personPanel.stylingOpen');
      if (saved !== null) {
        return JSON.parse(saved);
      }
    } catch {
      // Ignore JSON/Storage errors and fall back to default
    }
    return false;
  });

  const toggleSection = () => {
    const next = !isOpen;
    setIsOpen(next);
    try {
      localStorage.setItem('personPanel.stylingOpen', JSON.stringify(next));
    } catch {}
  };

  if (selectedPersons.length === 0) {
    return null;
  }

  const primaryPerson = selectedPersons[0];

  const handleStyleUpdate = (field: string, value: any) => {
    onExecute({
      personIds: selectedIds,
      styleUpdates: { [field]: value }
    });
  };

  return (
    <>
      <div className="px-3 pr-6 mt-4">
        <button
          data-testid="tid.properties.fill"
          type="button"
          onClick={toggleSection}
          className="flex items-center justify-between w-full mb-3 group"
        >
          <h3 className="text-[11px] font-semibold text-foreground uppercase tracking-wider">
            {t('stylePanel.title', 'Styling')}
          </h3>
          <div className="opacity-70 group-hover:opacity-100 transition-opacity">
            {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          </div>
        </button>

        {isOpen && (
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-normal text-foreground mb-1.5 block">
                {t('stylePanel.fillColor', 'Fill Color')}
              </Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={(context.selectionStyles?.fillColor as string) || primaryPerson?.fillColor || '#f5f5f5'}
                  onChange={(e) => handleStyleUpdate('fillColor', e.target.value)}
                  className="w-10 h-7 border border-border rounded cursor-pointer"
                />
                <Input
                  data-testid="tid.properties.fill.input"
                  value={(context.selectionStyles?.fillColor as string) || primaryPerson?.fillColor || '#f5f5f5'}
                  onChange={(e) => handleStyleUpdate('fillColor', e.target.value)}
                  className="flex-1 h-7 text-xs font-mono"
                  placeholder="#f5f5f5"
                />
              </div>
            </div>

            <div>
              <Label className="text-[11px] font-normal text-foreground mb-1.5 block">
                {t('stylePanel.borderColor', 'Border Color')}
              </Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={(context.selectionStyles?.strokeColor as string) || primaryPerson?.borderColor || '#000000'}
                  onChange={(e) => handleStyleUpdate('borderColor', e.target.value)}
                  className="w-10 h-7 border border-border rounded cursor-pointer"
                />
                <Input
                  data-testid="tid.properties.border.input"
                  value={(context.selectionStyles?.strokeColor as string) || primaryPerson?.borderColor || '#000000'}
                  onChange={(e) => handleStyleUpdate('borderColor', e.target.value)}
                  className="flex-1 h-7 text-xs font-mono"
                  placeholder="#000000"
                />
              </div>
            </div>

            <div>
              <Label className="text-[11px] font-normal text-foreground mb-1.5 block">
                {t('stylePanel.borderWidth', 'Border Width')}
              </Label>
              <Select
                value={(context.selectionStyles?.strokeWidth?.toString() as string) || primaryPerson?.borderWidth?.toString() || '1'}
                onValueChange={(value) => handleStyleUpdate('borderWidth', parseInt(value))}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1" className="text-xs">1px</SelectItem>
                  <SelectItem value="2" className="text-xs">2px</SelectItem>
                  <SelectItem value="3" className="text-xs">3px</SelectItem>
                  <SelectItem value="4" className="text-xs">4px</SelectItem>
                  <SelectItem value="5" className="text-xs">5px</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px] font-normal text-foreground mb-1.5 block">
                {t('stylePanel.state', 'State')}
              </Label>
              <div className="flex items-center justify-between">
                <span className="text-xs">{t('stylePanel.locked', 'Locked')}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStyleUpdate('locked', !primaryPerson?.locked)}
                  className="h-7"
                >
                  {primaryPerson?.locked ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <Unlock className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="h-px bg-border my-4" />
    </>
  );
};

/**
 * Domain-specific tool for styling person shapes in genogram diagrams.
 * Supports both single and multi-selection styling operations.
 */
export class PersonStyleTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-person-style',
    name: i18n.t('genogram:tools.personStyle', { defaultValue: 'Person Style' }) as string,
    icon: 'palette',
    category: 'style',
    description: 'Edit person appearance and style properties',
    version: '1.0.0',
    
    supportedDiagramTypes: ['genogram'],
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: false,
      contextMenu: true,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: false,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.personStyle', 'Person Style');
  }

  canExecute(context: IToolContext): boolean {
    if (!(super.canExecute(context) && context.selection.size > 0 && context.diagramType === 'genogram')) return false;
    const ids = Array.from(context.selection);
    const nodes = (useDiagramStore as any).getState().nodes as Array<any>;
    const selected = nodes.filter(n => ids.includes(n.id));
    if (selected.length === 0) return false;
    return selected.every(n => n.type === 'person');
  }

  async execute(params?: { personIds: string[]; styleUpdates: any }): Promise<ICommand | null> {
    if (!params?.personIds || !params?.styleUpdates) {
      return null;
    }

    console.log(`[PersonStyleTool] Execute called with ${params.personIds.length} nodes:`, params.personIds);
    console.log(`[PersonStyleTool] Style updates:`, params.styleUpdates);

    // Handle multiple style updates
    const styleFields = Object.keys(params.styleUpdates);
    
    if (styleFields.length === 1) {
      // Single style update
      const styleField = styleFields[0];
      const newValue = params.styleUpdates[styleField];
      return new ChangeNodeStyleCommand(params.personIds, styleField, newValue);
    } else {
      // Multiple style updates - create a composite command
      const { CompositeCommand } = await import('../../../core/commands/ICommand');
      const commands = styleFields.map(field => 
        new ChangeNodeStyleCommand(params.personIds, field, params.styleUpdates[field])
      );
      console.log(`[PersonStyleTool] Created ${commands.length} commands for composite`);
      return new CompositeCommand('Change Multiple Styles', commands);
    }
  }

  getPropertyPanelComponent() {
    return PersonStylePropertyComponent;
  }
}
